'use client';

import { Canvas, useFrame, useThree } from '@react-three/fiber';
import { useRef, useEffect, useState, useMemo, useCallback, memo } from 'react';
import type { ElementRef, MutableRefObject, RefObject } from 'react';
import { BufferGeometry, Group, Vector3, Quaternion, DoubleSide, Color, Line as ThreeLine, LineBasicMaterial } from 'three';
import { OrbitControls } from '@react-three/drei';
import * as topojson from 'topojson-client';
import { geoArea, geoCentroid, geoDistance } from 'd3-geo';
import * as THREE from 'three';
import { Earcut } from 'three/src/extras/Earcut.js';

type RegionStatus = 'available' | 'coming-soon' | 'unavailable';

interface RegionColors {
  [countryName: string]: RegionStatus;
}

interface InteractiveRegionGlobeProps {
  className?: string;
  speed?: number;
  interactive?: boolean;
  autoRotate?: boolean;
  regionColors: RegionColors;
  onCountryClick?: (countryName: string) => void;
  onCountryHover?: (countryName: string | null) => void;
  selectedCountry?: string | null;
  isDark?: boolean;
  /**
   * Fly the camera to this country and hold it there. `null` flies back out to
   * the resting view. Ignored unless `flightsEnabled` is true.
   */
  focusCountry?: string | null;
  /**
   * Fires once per focus request, after the camera has arrived. Also fires (on
   * a microtask, without moving the camera) when the name has no geometry, when
   * `reducedMotion` is on, or when `flightsEnabled` is false — a caller that
   * chains navigation off this callback can never be left hanging.
   *
   * NOT fired for a request that a newer `focusCountry` superseded: completing
   * an abandoned flight would navigate to the country the user just moved off.
   */
  onFlyComplete?: (country: string) => void;
  /** Fires after the return flight has landed and autorotation may resume. */
  onReturnComplete?: () => void;
  /** Master switch for the fly-to rig. Off means today's exact behavior. */
  flightsEnabled?: boolean;
  /**
   * Reports every country name the fly-to index resolved, straight from the
   * geometry. Callers whose own vocabulary differs (Region enums say "USA",
   * world-atlas says "United States of America") can pick the right spelling
   * from this instead of hand-keeping a second list that drifts. Fires only
   * when `flightsEnabled`; the set identity is stable per geometry load.
   */
  onFocusIndexReady?: (countryNames: ReadonlySet<string>) => void;
  /**
   * Force-skip flight animations. When omitted, the component falls back to the
   * `prefers-reduced-motion` media query.
   */
  reducedMotion?: boolean;
}

/** Camera radius at rest — matches the <Canvas> camera's initial z. */
const HOME_RADIUS = 2.5;
const FLIGHT_DURATION_MS = 900;
/** OrbitControls' resting floor. Flights dip below it, then restore it. */
const REST_MIN_DISTANCE = 1.8;
const FLIGHT_MIN_DISTANCE = 1.05;

/**
 * Framing distance = clamp(BASE + angularRadius * SCALE, MIN, MAX).
 *
 * Measured against the vendored world-atlas 110m data, this spreads the served
 * regions as: Belgium/Switzerland/Netherlands 1.30, France 1.42, Japan 1.47,
 * Mexico 1.63, Brazil 1.81, USA 1.83, Canada 1.89 — big countries hold back,
 * small ones come in close, and every one roughly fills the frame. At 1.95 the
 * globe's horizon almost exactly fills the 60° vertical fov, which is why that
 * is the outer clamp: past it the country starts shrinking inside empty sky.
 */
const FOCUS_DISTANCE_BASE = 1.25;
const FOCUS_DISTANCE_SCALE = 1.4;
const FOCUS_DISTANCE_MIN = 1.3;
const FOCUS_DISTANCE_MAX = 1.95;

/**
 * Polygons whose centroid sits farther than this (radians; ~26°) from the
 * country's largest landmass are dropped before computing the centroid and the
 * framing radius.
 *
 * This is NOT optional polish. world-atlas ships overseas territories inside
 * the parent country's MultiPolygon, so a naive geoCentroid() of the whole
 * feature aims at open water: France lands at [-6.8, 43.1] in the Atlantic
 * (French Guiana drags it) and the USA at [-103.6, 44.8] (Alaska + Hawaii).
 * Clustering fixes both — France [2.5, 46.5], USA [-98.7, 39.9] — while leaving
 * genuinely multi-island countries whole: New Zealand keeps both islands, Japan
 * keeps Honshu/Hokkaido/Kyushu, Indonesia keeps the whole archipelago.
 */
const TERRITORY_CLUSTER_RADIUS = 0.45;

const EMPTY_FOCUS_MAP: ReadonlyMap<string, CountryFocus> = new Map();

// Pre-computed color values for performance
const STATUS_COLORS = {
  available: { normal: new Color('#8dd4cd'), selected: new Color('#00a798') },
  'coming-soon': { normal: new Color('#fcd34d'), selected: new Color('#f59e0b') },
  unavailable: { normal: new Color('#c4c9cf'), selected: new Color('#e36798') },
} as const;

const OCEAN_COLOR = new Color('#4a90e2');
const BORDER_COLOR = new Color('#ffffff');
const HOVER_BORDER_COLOR = new Color('#00a798');

// Convert lat/lng to 3D sphere coordinates - optimized with pre-calculated constants
const DEG_TO_RAD = Math.PI / 180;
function latLngToVector3(lat: number, lng: number, radius: number): Vector3 {
  const phi = (90 - lat) * DEG_TO_RAD;
  const theta = (lng + 180) * DEG_TO_RAD;
  const sinPhi = Math.sin(phi);
  
  return new Vector3(
    -(radius * sinPhi * Math.cos(theta)),
    radius * Math.cos(phi),
    radius * sinPhi * Math.sin(theta)
  );
}

/** Camera aim + framing distance for one country, computed once on load. */
interface CountryFocus {
  /**
   * Unit vector at the country's centroid in the globe group's LOCAL space —
   * i.e. before the group's tilt and spin. Flight code MUST push this through
   * the group's world quaternion to turn it into a camera aim; see CameraFlyRig.
   */
  readonly dir: Vector3;
  /** Camera distance from the globe centre that frames the country. */
  readonly distance: number;
}

/**
 * Exact circumscribed angular radius: the largest great-circle distance from
 * the centroid to any vertex.
 *
 * Deliberately not geoBounds() corners — a lat/lng bbox corner is usually open
 * ocean rather than a real point of the country, so it over-estimates and the
 * camera hangs back too far. Measured over-estimates: India 0.396 vs 0.295 rad
 * (1.80 vs 1.66 distance), Australia 0.435 vs 0.361, Brazil 0.523 vs 0.403.
 */
function computeAngularRadius(geometry: GeoJSON.Geometry, centroid: [number, number]): number {
  let max = 0;
  const walkRings = (rings: GeoJSON.Position[][]) => {
    for (const ring of rings) {
      for (const point of ring) {
        const d = geoDistance(centroid, [point[0], point[1]]);
        if (d > max) max = d;
      }
    }
  };
  if (geometry.type === 'Polygon') walkRings(geometry.coordinates);
  else if (geometry.type === 'MultiPolygon') for (const poly of geometry.coordinates) walkRings(poly);
  return max;
}

/**
 * Drop remote overseas territories so centroid/framing describe the landmass a
 * viewer actually means. See TERRITORY_CLUSTER_RADIUS for why this is required.
 */
function mainLandmass(geometry: GeoJSON.Geometry): GeoJSON.Geometry {
  if (geometry.type !== 'MultiPolygon' || geometry.coordinates.length < 2) return geometry;

  const parts = geometry.coordinates.map((coordinates) => {
    const polygon: GeoJSON.Polygon = { type: 'Polygon', coordinates };
    return { coordinates, area: geoArea(polygon), centroid: geoCentroid(polygon) };
  });

  let seed = parts[0];
  for (const part of parts) if (part.area > seed.area) seed = part;

  const kept = parts.filter((p) => geoDistance(seed.centroid, p.centroid) <= TERRITORY_CLUSTER_RADIUS);
  if (kept.length === 0 || kept.length === parts.length) return geometry;

  return { type: 'MultiPolygon', coordinates: kept.map((p) => p.coordinates) };
}

function buildCountryFocusMap(countries: GeoJSON.FeatureCollection | null): ReadonlyMap<string, CountryFocus> {
  if (!countries) return EMPTY_FOCUS_MAP;

  const map = new Map<string, CountryFocus>();
  for (const feature of countries.features) {
    const name = feature.properties?.name as string | undefined;
    if (!name || !feature.geometry) continue;

    const core = mainLandmass(feature.geometry);
    const centroid = geoCentroid(core);
    if (!Number.isFinite(centroid[0]) || !Number.isFinite(centroid[1])) continue;

    const angularRadius = computeAngularRadius(core, centroid);
    const distance = Math.min(
      FOCUS_DISTANCE_MAX,
      Math.max(FOCUS_DISTANCE_MIN, FOCUS_DISTANCE_BASE + angularRadius * FOCUS_DISTANCE_SCALE),
    );

    // geoCentroid yields [lng, lat]; latLngToVector3 takes (lat, lng).
    map.set(name, { dir: latLngToVector3(centroid[1], centroid[0], 1), distance });
  }
  return map;
}

const easeInOutCubic = (t: number): number =>
  t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;

/**
 * Great-circle interpolation between two unit vectors, written into `out`.
 *
 * The camera path MUST be interpolated as a direction like this. Lerping the
 * raw camera positions instead traces a straight chord, which for any decent
 * angular travel passes inside radius 1 — the camera clips through the globe.
 */
function slerpDirection(from: Vector3, to: Vector3, t: number, out: Vector3): Vector3 {
  const dot = Math.min(1, Math.max(-1, from.dot(to)));

  if (dot > 0.999999) return out.copy(to);

  if (dot < -0.999999) {
    // Antipodal: no unique great circle, so pick any perpendicular axis.
    const axis = new Vector3(1, 0, 0);
    if (Math.abs(from.x) > 0.9) axis.set(0, 1, 0);
    axis.crossVectors(from, axis).normalize();
    return out.copy(from).applyAxisAngle(axis, t * Math.PI);
  }

  const theta = Math.acos(dot);
  const sinTheta = Math.sin(theta);
  const a = Math.sin((1 - t) * theta) / sinTheta;
  const b = Math.sin(t * theta) / sinTheta;
  return out
    .set(from.x * a + to.x * b, from.y * a + to.y * b, from.z * a + to.z * b)
    .normalize();
}

// Subdivide a triangle on the sphere to follow curvature
function subdivideSphericalTriangle(
  v1: Vector3, v2: Vector3, v3: Vector3, 
  radius: number, 
  depth: number,
  output: number[]
): void {
  if (depth === 0) {
    output.push(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z, v3.x, v3.y, v3.z);
    return;
  }
  
  const m12 = new Vector3().addVectors(v1, v2).multiplyScalar(0.5).normalize().multiplyScalar(radius);
  const m23 = new Vector3().addVectors(v2, v3).multiplyScalar(0.5).normalize().multiplyScalar(radius);
  const m31 = new Vector3().addVectors(v3, v1).multiplyScalar(0.5).normalize().multiplyScalar(radius);
  
  subdivideSphericalTriangle(v1, m12, m31, radius, depth - 1, output);
  subdivideSphericalTriangle(m12, v2, m23, radius, depth - 1, output);
  subdivideSphericalTriangle(m31, m23, v3, radius, depth - 1, output);
  subdivideSphericalTriangle(m12, m23, m31, radius, depth - 1, output);
}

// Calculate the angular size of a triangle
function getTriangleAngularSize(v1: Vector3, v2: Vector3, v3: Vector3): number {
  return Math.max(v1.angleTo(v2), v2.angleTo(v3), v3.angleTo(v1));
}

// Create mesh geometry with proper triangulation - returns both mesh and line geometries
function createCountryGeometries(coordinates: any, meshRadius: number, lineRadius: number): {
  meshGeometries: BufferGeometry[];
  lineGeometries: BufferGeometry[];
} {
  const meshGeometries: BufferGeometry[] = [];
  const lineGeometries: BufferGeometry[] = [];
  const subdivisionThreshold = 0.3;
  
  const processPolygon = (coords: any) => {
    if (!coords || coords.length === 0) return;
    
    const outerRing = coords[0];
    if (!outerRing || outerRing.length < 3) return;
    
    // Prepare flat array for earcut
    const flatCoords: number[] = [];
    const holeIndices: number[] = [];
    
    outerRing.forEach((coord: number[]) => {
      const [lng, lat] = coord;
      if (lat !== undefined && lng !== undefined) {
        flatCoords.push(lng, lat);
      }
    });
    
    // Add holes
    for (let i = 1; i < coords.length; i++) {
      const holeRing = coords[i];
      if (holeRing && holeRing.length >= 3) {
        holeIndices.push(flatCoords.length / 2);
        holeRing.forEach((coord: number[]) => {
          const [lng, lat] = coord;
          if (lat !== undefined && lng !== undefined) {
            flatCoords.push(lng, lat);
          }
        });
      }
    }
    
    if (flatCoords.length < 6) return;
    
    // Create line geometry for borders
    const linePoints: Vector3[] = [];
    for (let i = 0; i < outerRing.length; i++) {
      const [lng, lat] = outerRing[i];
      if (lat !== undefined && lng !== undefined) {
        linePoints.push(latLngToVector3(lat, lng, lineRadius));
      }
    }
    if (linePoints.length > 0) {
      const lineGeom = new BufferGeometry().setFromPoints(linePoints);
      lineGeometries.push(lineGeom);
    }
    
    // Triangulate
    const triangleIndices = Earcut.triangulate(flatCoords, holeIndices.length > 0 ? holeIndices : undefined, 2);
    if (triangleIndices.length === 0) return;
    
    // Convert to 3D
    const vertices3D: Vector3[] = [];
    for (let i = 0; i < flatCoords.length; i += 2) {
      vertices3D.push(latLngToVector3(flatCoords[i + 1], flatCoords[i], meshRadius));
    }
    
    // Process with subdivision
    const finalVertices: number[] = [];
    for (let i = 0; i < triangleIndices.length; i += 3) {
      const v1 = vertices3D[triangleIndices[i]];
      const v2 = vertices3D[triangleIndices[i + 1]];
      const v3 = vertices3D[triangleIndices[i + 2]];
      
      if (!v1 || !v2 || !v3) continue;
      
      const angularSize = getTriangleAngularSize(v1, v2, v3);
      let depth = 0;
      if (angularSize > subdivisionThreshold * 4) depth = 3;
      else if (angularSize > subdivisionThreshold * 2) depth = 2;
      else if (angularSize > subdivisionThreshold) depth = 1;
      
      if (depth > 0) {
        subdivideSphericalTriangle(v1, v2, v3, meshRadius, depth, finalVertices);
      } else {
        finalVertices.push(v1.x, v1.y, v1.z, v2.x, v2.y, v2.z, v3.x, v3.y, v3.z);
      }
    }
    
    if (finalVertices.length > 0) {
      const geometry = new BufferGeometry();
      geometry.setAttribute('position', new THREE.Float32BufferAttribute(finalVertices, 3));
      geometry.computeVertexNormals();
      meshGeometries.push(geometry);
    }
  };

  if (coordinates.type === 'Polygon') {
    processPolygon(coordinates.coordinates);
  } else if (coordinates.type === 'MultiPolygon') {
    coordinates.coordinates.forEach((polygon: any) => processPolygon(polygon));
  }
  
  return { meshGeometries, lineGeometries };
}

/** Border polylines — raycast disabled so hits reach country fill meshes below. */
const CountryBorderLine = memo(function CountryBorderLine({
  geometry,
  hovered,
}: {
  geometry: BufferGeometry;
  hovered: boolean;
}) {
  const lineObject = useMemo(() => {
    const mat = new LineBasicMaterial({
      color: BORDER_COLOR.clone(),
      transparent: true,
      opacity: 0.7,
    });
    const line = new ThreeLine(geometry, mat);
    line.raycast = () => {};
    return line;
  }, [geometry]);

  useEffect(() => {
    const mat = lineObject.material as LineBasicMaterial;
    mat.color.copy(hovered ? HOVER_BORDER_COLOR : BORDER_COLOR);
    mat.opacity = hovered ? 1 : 0.7;
  }, [hovered, lineObject]);

  return <primitive object={lineObject} />;
});

// Memoized country mesh component
const CountryMesh = memo(function CountryMesh({ 
  geometries,
  color,
  onClick,
  onPointerOver,
  onPointerOut,
  onGlobeSurfaceEnter,
  onGlobeSurfaceLeave,
}: { 
  geometries: { meshGeometries: BufferGeometry[]; lineGeometries: BufferGeometry[] };
  color: Color;
  onClick: () => void;
  onPointerOver: () => void;
  onPointerOut: () => void;
  onGlobeSurfaceEnter?: () => void;
  onGlobeSurfaceLeave?: () => void;
}) {
  const groupRef = useRef<Group>(null);
  const [hovered, setHovered] = useState(false);
  const pointerDownRef = useRef<{ x: number; y: number; time: number } | null>(null);
  
  // Memoize materials to prevent recreation
  const meshMaterial = useMemo(() => (
    <meshStandardMaterial 
      color={color}
      side={DoubleSide}
      metalness={0.1}
      roughness={0.6}
      polygonOffset
      polygonOffsetFactor={-1}
      polygonOffsetUnits={-1}
    />
  ), [color]);
  
  // Click detection using pointer down/up with distance and time threshold
  const handlePointerDown = useCallback((e: any) => {
    e.stopPropagation();
    pointerDownRef.current = { x: e.clientX, y: e.clientY, time: Date.now() };
  }, []);

  const handlePointerUp = useCallback((e: any) => {
    e.stopPropagation();
    if (pointerDownRef.current) {
      const deltaX = Math.abs(e.clientX - pointerDownRef.current.x);
      const deltaY = Math.abs(e.clientY - pointerDownRef.current.y);
      const deltaTime = Date.now() - pointerDownRef.current.time;
      
      // Click = small movement (< 8px) AND quick press (< 300ms)
      const isClick = deltaX < 8 && deltaY < 8 && deltaTime < 300;
      
      if (isClick) {
        onClick();
      }
    }
    pointerDownRef.current = null;
  }, [onClick]);

  const handlePointerOver = useCallback((e: any) => {
    e.stopPropagation();
    setHovered(true);
    onGlobeSurfaceEnter?.();
    onPointerOver();
  }, [onPointerOver, onGlobeSurfaceEnter]);

  const handlePointerOut = useCallback((e: any) => {
    e.stopPropagation();
    setHovered(false);
    onGlobeSurfaceLeave?.();
    onPointerOut();
    pointerDownRef.current = null;
  }, [onPointerOut, onGlobeSurfaceLeave]);

  // Update cursor only when hover changes
  useEffect(() => {
    document.body.style.cursor = hovered ? 'pointer' : 'auto';
    return () => { document.body.style.cursor = 'auto'; };
  }, [hovered]);

  return (
    <group ref={groupRef}>
      {geometries.meshGeometries.map((geometry, i) => (
        <mesh
          key={`m${i}`}
          geometry={geometry}
          onPointerDown={handlePointerDown}
          onPointerUp={handlePointerUp}
          onPointerOver={handlePointerOver}
          onPointerOut={handlePointerOut}
        >
          {meshMaterial}
        </mesh>
      ))}
      {geometries.lineGeometries.map((geometry, i) => (
        <CountryBorderLine key={`l${i}`} geometry={geometry} hovered={hovered} />
      ))}
    </group>
  );
});

// Memoized ocean sphere — absorbs pointer events so clicks/hovers cannot
// pass through to country meshes on the far side of the globe.
const OceanSphere = memo(function OceanSphere({
  onGlobeSurfaceEnter,
  onGlobeSurfaceLeave,
}: {
  onGlobeSurfaceEnter?: () => void;
  onGlobeSurfaceLeave?: () => void;
}) {
  const geometry = useMemo(() => new THREE.SphereGeometry(1, 48, 48), []);
  const material = useMemo(() => (
    <meshStandardMaterial color={OCEAN_COLOR} metalness={0.3} roughness={0.7} />
  ), []);

  const handlePointerOver = useCallback((e: any) => {
    e.stopPropagation();
    onGlobeSurfaceEnter?.();
  }, [onGlobeSurfaceEnter]);

  const handlePointerOut = useCallback((e: any) => {
    e.stopPropagation();
    onGlobeSurfaceLeave?.();
  }, [onGlobeSurfaceLeave]);

  const stop = useCallback((e: any) => {
    e.stopPropagation();
  }, []);

  return (
    <mesh
      geometry={geometry}
      onPointerOver={handlePointerOver}
      onPointerOut={handlePointerOut}
      onPointerDown={stop}
      onPointerUp={stop}
      onPointerMove={stop}
      onClick={stop}
    >
      {material}
    </mesh>
  );
});

// Globe with countries - optimized
const GlobeWithCountries = memo(function GlobeWithCountries({
  speed = 1,
  autoRotate = true,
  countries,
  regionColors,
  onCountryClick,
  onCountryHover,
  selectedCountry,
  onGlobeSurfaceHoverChange,
  groupRef,
  freezeRotationRef,
}: {
  speed?: number;
  autoRotate?: boolean;
  countries: GeoJSON.FeatureCollection | null;
  regionColors: RegionColors;
  onCountryClick?: (countryName: string) => void;
  onCountryHover?: (countryName: string | null) => void;
  selectedCountry?: string | null;
  onGlobeSurfaceHoverChange?: (hovering: boolean) => void;
  /** Owned by the parent so the camera rig can read the live spin. */
  groupRef: RefObject<Group>;
  /** Set by the camera rig; see the useFrame note below. */
  freezeRotationRef: MutableRefObject<boolean>;
}) {
  const globeSurfaceHoverCountRef = useRef(0);

  const flushGlobeSurfaceHover = useCallback(() => {
    queueMicrotask(() => {
      onGlobeSurfaceHoverChange?.(globeSurfaceHoverCountRef.current > 0);
    });
  }, [onGlobeSurfaceHoverChange]);

  const onGlobeSurfaceEnter = useCallback(() => {
    globeSurfaceHoverCountRef.current += 1;
    flushGlobeSurfaceHover();
  }, [flushGlobeSurfaceHover]);

  const onGlobeSurfaceLeave = useCallback(() => {
    globeSurfaceHoverCountRef.current = Math.max(0, globeSurfaceHoverCountRef.current - 1);
    flushGlobeSurfaceHover();
  }, [flushGlobeSurfaceHover]);

  // Optimized animation - only run when autoRotate is true.
  //
  // freezeRotationRef is checked per frame rather than via a prop because a
  // fly-to aims at where the country is at the instant the flight starts. If
  // the group kept spinning, the target would slide out from under the camera
  // and the country would land off-centre. The parent also drops autoRotate
  // during a flight; this ref is the frame-accurate belt to that braces.
  useFrame((_, delta) => {
    if (groupRef.current && autoRotate && !freezeRotationRef.current) {
      groupRef.current.rotation.y += delta * 0.08 * speed;
    }
  });

  useEffect(() => {
    if (groupRef.current) {
      groupRef.current.rotation.x = Math.PI * 0.08;
    }
  }, [groupRef]);

  // Pre-compute all geometries once when geoData loads
  const countryGeometriesMap = useMemo(() => {
    if (!countries) return new Map();
    
    const map = new Map<string, { meshGeometries: BufferGeometry[]; lineGeometries: BufferGeometry[] }>();
    countries.features.forEach((feature: GeoJSON.Feature) => {
      const name = feature.properties?.name as string;
      if (name && feature.geometry) {
        map.set(name, createCountryGeometries(feature.geometry, 1.01, 1.015));
      }
    });
    return map;
  }, [countries]);

  // Memoize click handlers
  const clickHandlers = useMemo(() => {
    if (!countries) return new Map();
    const map = new Map<string, () => void>();
    countries.features.forEach((feature: GeoJSON.Feature) => {
      const name = feature.properties?.name as string;
      if (name) {
        map.set(name, () => onCountryClick?.(name));
      }
    });
    return map;
  }, [countries, onCountryClick]);

  // Memoize hover handlers
  const hoverInHandlers = useMemo(() => {
    if (!countries) return new Map();
    const map = new Map<string, () => void>();
    countries.features.forEach((feature: GeoJSON.Feature) => {
      const name = feature.properties?.name as string;
      if (name) {
        map.set(name, () => onCountryHover?.(name));
      }
    });
    return map;
  }, [countries, onCountryHover]);

  const handleHoverOut = useCallback(() => onCountryHover?.(null), [onCountryHover]);

  if (!countries) return null;

  return (
    <group ref={groupRef}>
      <OceanSphere
        onGlobeSurfaceEnter={onGlobeSurfaceEnter}
        onGlobeSurfaceLeave={onGlobeSurfaceLeave}
      />
      {countries.features.map((feature: GeoJSON.Feature, i: number) => {
        const countryName = feature.properties?.name as string;
        if (!countryName) return null;
        
        const status = regionColors[countryName] || 'unavailable';
        const isSelected = selectedCountry === countryName;
        const colorSet = STATUS_COLORS[status] || STATUS_COLORS.unavailable;
        const color = isSelected ? colorSet.selected : colorSet.normal;
        const geometries = countryGeometriesMap.get(countryName);
        
        if (!geometries) return null;

        return (
          <CountryMesh
            key={i}
            geometries={geometries}
            color={color}
            onClick={clickHandlers.get(countryName) || (() => {})}
            onPointerOver={hoverInHandlers.get(countryName) || (() => {})}
            onPointerOut={handleHoverOut}
            onGlobeSurfaceEnter={onGlobeSurfaceEnter}
            onGlobeSurfaceLeave={onGlobeSurfaceLeave}
          />
        );
      })}
    </group>
  );
});

type OrbitControlsRef = ElementRef<typeof OrbitControls>;

interface Flight {
  startedAt: number;
  fromDir: Vector3;
  fromRadius: number;
  toDir: Vector3;
  toRadius: number;
  /** Country to report on arrival; null marks the return flight. */
  country: string | null;
}

/**
 * Drives the camera to a country and back.
 *
 * THE THING NOT TO BREAK: the countries rotate as a GROUP; the camera does not
 * orbit them. A country's stored `dir` is therefore in group-local space and
 * only becomes a camera aim after being pushed through the group's world
 * quaternion. Because that quaternion is sampled once, at flight start, the
 * group spin must be frozen for the whole flight (freezeRotationRef) or the aim
 * drifts out from under the camera.
 */
const CameraFlyRig = memo(function CameraFlyRig({
  focusCountry,
  focusMap,
  globeGroupRef,
  controlsRef,
  freezeRotationRef,
  reducedMotion,
  onFlyComplete,
  onReturnComplete,
  onAnimatingChange,
  onEngagedChange,
}: {
  focusCountry: string | null;
  focusMap: ReadonlyMap<string, CountryFocus>;
  globeGroupRef: RefObject<Group>;
  controlsRef: RefObject<OrbitControlsRef>;
  freezeRotationRef: MutableRefObject<boolean>;
  reducedMotion: boolean;
  onFlyComplete?: (country: string) => void;
  onReturnComplete?: () => void;
  onAnimatingChange: (animating: boolean) => void;
  onEngagedChange: (engaged: boolean) => void;
}) {
  const camera = useThree((s) => s.camera);
  const invalidate = useThree((s) => s.invalidate);

  const flightRef = useRef<Flight | null>(null);
  /** Camera direction before the first fly-to, so the return lands exactly back. */
  const homeDirRef = useRef<Vector3 | null>(null);
  /** True from the first fly-to until the return lands (not just while moving). */
  const engagedRef = useRef(false);
  const requestedRef = useRef<string | null>(null);

  const scratchDir = useRef(new Vector3()).current;
  const scratchQuat = useRef(new Quaternion()).current;

  // Callbacks via refs: the flight effect must not re-run just because a parent
  // re-render produced new inline handler identities.
  const onFlyCompleteRef = useRef(onFlyComplete);
  const onReturnCompleteRef = useRef(onReturnComplete);
  useEffect(() => {
    onFlyCompleteRef.current = onFlyComplete;
    onReturnCompleteRef.current = onReturnComplete;
  }, [onFlyComplete, onReturnComplete]);

  const applyCamera = useCallback(
    (dir: Vector3, radius: number) => {
      camera.position.copy(dir).multiplyScalar(radius);
      const controls = controlsRef.current;
      if (controls) {
        controls.target.set(0, 0, 0);
        // drei only calls update() while controls.enabled, and we disable them
        // for the flight — so this is the only thing keeping the controls'
        // internal spherical state in sync with where we put the camera. Skip
        // it and the user's next drag snaps back to the pre-flight position.
        controls.update();
      } else {
        camera.lookAt(0, 0, 0);
      }
    },
    [camera, controlsRef],
  );

  const finishFlight = useCallback(
    (country: string | null) => {
      onAnimatingChange(false);
      const controls = controlsRef.current;
      if (controls) controls.enabled = true;

      if (country === null) {
        engagedRef.current = false;
        homeDirRef.current = null;
        freezeRotationRef.current = false;
        onEngagedChange(false);
        // Only safe to restore the resting floor now that we are back at 2.5.
        if (controls) controls.minDistance = REST_MIN_DISTANCE;
        queueMicrotask(() => onReturnCompleteRef.current?.());
        return;
      }

      // Arrival. minDistance deliberately stays at FLIGHT_MIN_DISTANCE: the
      // camera is parked at 1.30-1.95, so restoring 1.8 here would make the
      // next controls.update() clamp it straight back out — a visible pop on
      // every country closer than 1.8 (France, Japan, Germany, ...).
      queueMicrotask(() => onFlyCompleteRef.current?.(country));
    },
    [controlsRef, freezeRotationRef, onAnimatingChange, onEngagedChange],
  );

  const beginFlight = useCallback(
    (toDir: Vector3, toRadius: number, country: string | null, instant: boolean) => {
      const controls = controlsRef.current;

      freezeRotationRef.current = true;
      if (!engagedRef.current) {
        engagedRef.current = true;
        onEngagedChange(true);
        const length = camera.position.length();
        homeDirRef.current =
          length > 1e-6 ? camera.position.clone().divideScalar(length) : new Vector3(0, 0, 1);
      }

      if (controls) {
        controls.enabled = false;
        controls.autoRotate = false;
        controls.minDistance = FLIGHT_MIN_DISTANCE;
        controls.target.set(0, 0, 0);
      }

      if (instant) {
        flightRef.current = null;
        applyCamera(toDir, toRadius);
        finishFlight(country);
        invalidate();
        return;
      }

      // Always start from where the camera is right now, which is what makes a
      // mid-flight retarget bend smoothly instead of stacking animations.
      const fromRadius = camera.position.length() || HOME_RADIUS;
      flightRef.current = {
        startedAt: performance.now(),
        fromDir: camera.position.clone().divideScalar(fromRadius),
        fromRadius,
        toDir: toDir.clone(),
        toRadius,
        country,
      };
      onAnimatingChange(true);
      invalidate();
    },
    [applyCamera, camera, controlsRef, finishFlight, freezeRotationRef, invalidate, onAnimatingChange, onEngagedChange],
  );

  useEffect(() => {
    if (focusCountry === requestedRef.current) return;
    requestedRef.current = focusCountry;

    if (focusCountry) {
      const focus = focusMap.get(focusCountry);
      if (!focus) {
        console.warn(
          `[InteractiveRegionGlobe] focusCountry "${focusCountry}" matches no country geometry — completing without a fly-to.`,
        );
        queueMicrotask(() => onFlyCompleteRef.current?.(focusCountry));
        return;
      }

      // Group-local centroid -> world aim. updateWorldMatrix keeps this exact
      // even though useFrame may not have run since the last spin increment.
      const group = globeGroupRef.current;
      const worldDir = focus.dir.clone();
      if (group) {
        group.updateWorldMatrix(true, false);
        group.getWorldQuaternion(scratchQuat);
        worldDir.applyQuaternion(scratchQuat).normalize();
      }
      beginFlight(worldDir, focus.distance, focusCountry, reducedMotion);
      return;
    }

    if (!engagedRef.current) return;
    beginFlight(homeDirRef.current ?? new Vector3(0, 0, 1), HOME_RADIUS, null, reducedMotion);
  }, [focusCountry, focusMap, reducedMotion, beginFlight, globeGroupRef, scratchQuat]);

  useFrame(() => {
    const flight = flightRef.current;
    if (!flight) return;

    const t = Math.min(1, Math.max(0, (performance.now() - flight.startedAt) / FLIGHT_DURATION_MS));
    const eased = easeInOutCubic(t);

    slerpDirection(flight.fromDir, flight.toDir, eased, scratchDir);
    applyCamera(scratchDir, flight.fromRadius + (flight.toRadius - flight.fromRadius) * eased);

    if (t >= 1) {
      flightRef.current = null;
      finishFlight(flight.country);
    } else {
      // Keeps the flight self-sustaining even if frameloop is still 'demand'.
      invalidate();
    }
  });

  // Never strand the controls in their flight configuration.
  useEffect(
    () => () => {
      const controls = controlsRef.current;
      if (controls) {
        controls.enabled = true;
        controls.minDistance = REST_MIN_DISTANCE;
      }
      freezeRotationRef.current = false;
    },
    [controlsRef, freezeRotationRef],
  );

  return null;
});

export function InteractiveRegionGlobe({
  className = '',
  speed = 1,
  interactive = false,
  autoRotate = true,
  regionColors,
  onCountryClick,
  onCountryHover,
  selectedCountry,
  isDark = false,
  focusCountry = null,
  onFlyComplete,
  onReturnComplete,
  flightsEnabled = false,
  reducedMotion,
  onFocusIndexReady,
}: InteractiveRegionGlobeProps) {
  const [isInteracting, setIsInteracting] = useState(false);
  const [globeSurfaceHovered, setGlobeSurfaceHovered] = useState(false);
  const [geoData, setGeoData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  /** True only while a flight is animating — drives frameloop. */
  const [flightAnimating, setFlightAnimating] = useState(false);
  /** True from the first fly-to until the return lands — suppresses autorotate. */
  const [cameraEngaged, setCameraEngaged] = useState(false);
  const [systemReducedMotion, setSystemReducedMotion] = useState(false);

  const globeGroupRef = useRef<Group>(null);
  const controlsRef = useRef<OrbitControlsRef>(null);
  const freezeRotationRef = useRef(false);

  useEffect(() => {
    // Self-hosted, byte-identical to world-atlas@2.0.2/countries-110m.json and
    // served immutable by app/api/maps/[filename]/route.ts. Country name
    // strings must stay identical: regionColors is keyed on them.
    fetch('/api/maps/world-countries-110m.json')
      .then(response => response.json())
      .then(data => {
        setGeoData(data);
        setLoading(false);
      })
      .catch(error => {
        console.error('Error loading geography data:', error);
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    const apply = () => setSystemReducedMotion(query.matches);
    apply();
    query.addEventListener('change', apply);
    return () => query.removeEventListener('change', apply);
  }, []);

  // Feature extraction lives here (not in GlobeWithCountries) so the mesh
  // builder and the focus map share one parse of the topology.
  const countries = useMemo(() => {
    if (!geoData) return null;
    const result: unknown = topojson.feature(geoData, geoData.objects.countries);
    // topojson.feature returns FeatureCollection when given a geometry collection
    const collection = result as GeoJSON.FeatureCollection | null;
    if (collection && collection.type === 'FeatureCollection' && Array.isArray(collection.features)) {
      return collection;
    }
    return null;
  }, [geoData]);

  const focusMap = useMemo(
    () => (flightsEnabled ? buildCountryFocusMap(countries) : EMPTY_FOCUS_MAP),
    [flightsEnabled, countries],
  );

  // Memoized so the identity is stable per geometry load: repeat reports then
  // hit React's bail-out instead of re-rendering the caller.
  const focusableCountries = useMemo<ReadonlySet<string>>(
    () => new Set(focusMap.keys()),
    [focusMap],
  );

  useEffect(() => {
    if (flightsEnabled && focusableCountries.size > 0) onFocusIndexReady?.(focusableCountries);
  }, [flightsEnabled, focusableCountries, onFocusIndexReady]);

  // Stable callbacks for OrbitControls
  const handleInteractionStart = useCallback(() => setIsInteracting(true), []);
  const handleInteractionEnd = useCallback(() => {
    setTimeout(() => setIsInteracting(false), 100);
  }, []);

  // Handle country hover and track for cursor changes
  const handleCountryHover = useCallback((countryName: string | null) => {
    setHoveredCountry(countryName);
    onCountryHover?.(countryName);
  }, [onCountryHover]);

  const handleGlobeSurfaceHoverChange = useCallback((hovering: boolean) => {
    setGlobeSurfaceHovered(hovering);
  }, []);

  // cameraEngaged (not merely flightAnimating) stays true while parked on a
  // country, so the globe cannot spin the country back out of frame after the
  // camera has arrived.
  const effectiveAutoRotate =
    autoRotate && !isInteracting && !globeSurfaceHovered && !cameraEngaged;

  const effectiveReducedMotion = reducedMotion ?? systemReducedMotion;

  // Determine cursor style based on hover state and region availability
  const getCursorStyle = useMemo(() => {
    if (isInteracting) return 'grabbing';
    
    if (hoveredCountry) {
      const status = regionColors[hoveredCountry];
      if (status === 'available' || status === 'coming-soon') {
        return 'pointer'; // Distinct cursor for available/coming-soon regions
      }
    }
    
    return interactive ? 'grab' : 'default';
  }, [isInteracting, hoveredCountry, regionColors, interactive]);

  // Memoize canvas style
  const canvasStyle = useMemo(() => ({ 
    width: '100%', 
    height: '100%',
    background: 'transparent',
    cursor: getCursorStyle,
  }), [getCursorStyle]);

  // Memoize GL config
  const glConfig = useMemo(() => ({ 
    alpha: true, 
    antialias: true,
    powerPreference: 'high-performance' as const,
    stencil: false,
    depth: true,
  }), []);

  // Memoize light intensities
  const ambientIntensity = isDark ? 0.6 : 1.2;
  const mainLightIntensity = isDark ? 1.2 : 1.8;
  const fillLightIntensity = isDark ? 0.5 : 0.8;

  if (loading || !geoData) {
    return (
      <div className={className}>
        <div className="flex items-center justify-center h-full w-full">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#00a798] mx-auto mb-4"></div>
            <p className="text-gray-600 dark:text-gray-400">Loading Globe...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <Canvas
        camera={{ position: [0, 0, 2.5], fov: 60 }}
        style={canvasStyle}
        gl={glConfig}
        dpr={[1, 1.5]} // Limit pixel ratio for performance
        frameloop={
          autoRotate || isInteracting || globeSurfaceHovered || flightAnimating ? 'always' : 'demand'
        }
      >
        <ambientLight intensity={ambientIntensity} />
        <directionalLight position={[5, 3, 5]} intensity={mainLightIntensity} />
        <directionalLight position={[-3, 1, -2]} intensity={fillLightIntensity} />

        <GlobeWithCountries
          speed={speed}
          autoRotate={effectiveAutoRotate}
          countries={countries}
          regionColors={regionColors}
          onCountryClick={onCountryClick}
          onCountryHover={handleCountryHover}
          selectedCountry={selectedCountry}
          onGlobeSurfaceHoverChange={handleGlobeSurfaceHoverChange}
          groupRef={globeGroupRef}
          freezeRotationRef={freezeRotationRef}
        />

        {flightsEnabled && (
          <CameraFlyRig
            focusCountry={focusCountry}
            focusMap={focusMap}
            globeGroupRef={globeGroupRef}
            controlsRef={controlsRef}
            freezeRotationRef={freezeRotationRef}
            reducedMotion={effectiveReducedMotion}
            onFlyComplete={onFlyComplete}
            onReturnComplete={onReturnComplete}
            onAnimatingChange={setFlightAnimating}
            onEngagedChange={setCameraEngaged}
          />
        )}

        {interactive && (
          <OrbitControls
            ref={controlsRef}
            enableZoom={true}
            zoomSpeed={0.7}
            enablePan={false}
            // These two mirror what CameraFlyRig writes imperatively. The rig
            // needs frame accuracy, React needs to not overwrite it on the next
            // render — so both paths must always agree.
            enabled={!flightAnimating}
            minDistance={cameraEngaged ? FLIGHT_MIN_DISTANCE : REST_MIN_DISTANCE}
            maxDistance={4}
            autoRotate={effectiveAutoRotate}
            autoRotateSpeed={speed * 0.3}
            rotateSpeed={0.6}
            dampingFactor={0.15}
            enableDamping={true}
            onStart={handleInteractionStart}
            onEnd={handleInteractionEnd}
          />
        )}
      </Canvas>
    </div>
  );
}

